// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Product grid with category tabs
// ---------------------------------------------------------------------------

import { useState, useMemo } from 'react';
import type { Product } from '@/lib/types';
import ProductButton from '@/components/ProductButton';

interface ProductGridProps {
  products: Product[];
  onProductClick: (product: Product) => void;
}

export default function ProductGrid({ products, onProductClick }: ProductGridProps) {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const p of products) {
      if (p.category_name) cats.add(p.category_name);
      else if (p.category) cats.add(p.category);
    }
    return ['all', ...Array.from(cats).sort()];
  }, [products]);

  const filtered = useMemo(() => {
    if (activeCategory === 'all') return products;
    return products.filter(
      (p) => p.category_name === activeCategory || p.category === activeCategory,
    );
  }, [products, activeCategory]);

  if (products.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
        Aucun produit disponible
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Category tabs */}
      {categories.length > 2 && (
        <div className="flex gap-2 px-3 py-2 overflow-x-auto shrink-0 scrollbar-none">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                activeCategory === cat
                  ? 'bg-accent text-white'
                  : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
              }`}
            >
              {cat === 'all' ? 'Tous' : cat}
            </button>
          ))}
        </div>
      )}

      {/* Product grid */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {filtered.map((product) => (
            <ProductButton
              key={product.key ?? product.id}
              product={product}
              onClick={() => onProductClick(product)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
